import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Wind,
  Flame,
  Droplets,
  Check,
} from 'lucide-react';
import apiService from '../../services/api';
import { useTreatment } from '../../context/TreatmentContext';
import TreatmentSubHeader from '../../components/TreatmentSubHeader';
import '../../styles/treatment.css';
import '../../styles/prakriti-assess.css';

const DOSHA_PROFILES = {
  'Vata': {
    description: 'Vata represents the elements of Space and Air. It governs all movement in the body and mind, including blood flow, breathing, and thoughts.',
    qualities: 'Dry, light, cold, rough, subtle, mobile, and clear.',
    diet: 'Favor warm, cooked, nourishing meals with sweet, sour, and salty tastes. Limit raw, dry, cold foods.',
    lifestyle: 'Maintain a regular daily routine, keep warm, prioritize restful sleep, and practice calming meditation.'
  },
  'Pitta': {
    description: 'Pitta represents the elements of Fire and Water. It governs digestion, metabolism, energy production, and cognitive processing.',
    qualities: 'Hot, sharp, light, liquid, spreading, and oily.',
    diet: 'Favor cooling, fresh foods with sweet, bitter, and astringent tastes. Avoid excessively spicy, salty, and sour foods.',
    lifestyle: 'Avoid mid-day sun, maintain a balanced work-rest schedule, and cultivate patience and moderation.'
  },
  'Kapha': {
    description: 'Kapha represents the elements of Water and Earth. It governs physical structure, hydration, lubrication, and immune resistance.',
    qualities: 'Heavy, slow, cool, oily, smooth, dense, soft, and stable.',
    diet: 'Favor warm, light, spicy, and dry foods with pungent, bitter, and astringent tastes. Avoid sweet, heavy, cold foods.',
    lifestyle: 'Wake up early, stay physically active, seek dry warmth, and embrace spontaneity and variety.'
  }
};

const OPTION_SCORES = { vata: [1, 0, 0], pitta: [0, 1, 0], kapha: [0, 0, 1] };

const DOSHA_CONFIG = {
  vata: {
    label: 'Vata',
    icon: Wind,
    color: 'var(--prakriti-vata)',
    bg: 'var(--prakriti-vata-bg)',
    border: 'var(--prakriti-vata-border)',
  },
  pitta: {
    label: 'Pitta',
    icon: Flame,
    color: 'var(--prakriti-pitta)',
    bg: 'var(--prakriti-pitta-bg)',
    border: 'var(--prakriti-pitta-border)',
  },
  kapha: {
    label: 'Kapha',
    icon: Droplets,
    color: 'var(--prakriti-kapha)',
    bg: 'var(--prakriti-kapha-bg)',
    border: 'var(--prakriti-kapha-border)',
  },
};

const PrakritiAssess = () => {
  const { currentPatient } = useTreatment();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getPrakritiQuestions();
      setQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const progress = totalQuestions ? ((currentIndex + 1) / totalQuestions) * 100 : 0;
  const answeredCount = questions.filter((q) => answers[q.id]).length;

  const handleAnswer = (questionId, choice) => {
    setAnswers((prev) => ({ ...prev, [questionId]: choice }));
  };

  const goNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setDirection(1);
      setCurrentIndex((i) => i + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex((i) => i - 1);
    }
  };

  const computeScores = () => {
    let v = 0, p = 0, k = 0;
    questions.forEach((q) => {
      const choice = answers[q.id];
      if (!choice) return;
      const scores = OPTION_SCORES[choice] || [0, 0, 0];
      v += scores[0];
      p += scores[1];
      k += scores[2];
    });
    const total = v + p + k || 1;
    return {
      vataScore: Math.round((v / total) * 100),
      pittaScore: Math.round((p / total) * 100),
      kaphaScore: Math.round((k / total) * 100),
    };
  };

  const getDominantPrakriti = (vataScore, pittaScore, kaphaScore) => {
    const arr = [
      { name: 'Vata', score: vataScore },
      { name: 'Pitta', score: pittaScore },
      { name: 'Kapha', score: kaphaScore },
    ].sort((a, b) => b.score - a.score);
    if (arr[0].score > arr[1].score + 10) return arr[0].name;
    if (arr[0].score > arr[1].score) return `${arr[0].name}-${arr[1].name}`;
    return 'Tridosha';
  };

  const handleSubmit = async () => {
    if (!currentPatient?.id) {
      setSubmitError('Please select a patient first.');
      return;
    }
    if (answeredCount < totalQuestions) {
      setSubmitError('Please answer all questions.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const { vataScore, pittaScore, kaphaScore } = computeScores();
      const dominantPrakriti = getDominantPrakriti(vataScore, pittaScore, kaphaScore);
      const payload = {
        patientId: currentPatient.id,
        responses: JSON.stringify(answers),
        vataScore,
        pittaScore,
        kaphaScore,
        dominantPrakriti,
      };
      const data = await apiService.submitPrakritiAssessment(payload);
      setResult({ ...data, dominantPrakriti, vataScore, pittaScore, kaphaScore });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentPatient) {
    return (
      <div className="treatment-page">
        <section className="hero">
          <div className="container">
            <div className="hero-content text-center">
              <h1>Prakriti Assessment</h1>
              <p>Complete a short questionnaire to determine your dosha constitution.</p>
            </div>
          </div>
        </section>
        <TreatmentSubHeader />
        <section className="section">
          <div className="container">
            <div className="treatment-card treatment-card-cta">
              <AlertCircle size={24} />
              <div>
                <h3>Select a patient first</h3>
                <p>Choose or create a patient profile to start the Prakriti assessment.</p>
                <Link to="/treatment/patients" className="btn btn-primary">Go to Patients</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (submitted && result) {
    const getDoshaProfiles = (dominant) => {
      if (!dominant) return [];
      if (dominant === 'Tridosha') {
        return [
          { name: 'Vata', ...DOSHA_PROFILES['Vata'] },
          { name: 'Pitta', ...DOSHA_PROFILES['Pitta'] },
          { name: 'Kapha', ...DOSHA_PROFILES['Kapha'] }
        ];
      }
      return dominant.split('-').map(p => ({
        name: p,
        ...DOSHA_PROFILES[p]
      })).filter(x => x.description);
    };

    return (
      <div className="treatment-page prakriti-result-page">
        <section className="hero">
          <div className="container">
            <div className="hero-content text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="prakriti-result-badge"
              >
                <Sparkles size={40} />
              </motion.div>
              <h1>Assessment complete</h1>
              <p>Your Prakriti has been recorded.</p>
            </div>
          </div>
        </section>
        <TreatmentSubHeader />
        <section className="section">
          <div className="container container-narrow">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.35 }}
              className="prakriti-result-card"
            >
              <h2 className="prakriti-result-dominant">{result.dominantPrakriti}</h2>
              <p className="prakriti-result-subtitle">Your dominant constitution</p>
              <div className="prakriti-score-bars">
                <div className="prakriti-score-row">
                  <span className="prakriti-score-label">
                    <Wind size={18} /> Vata
                  </span>
                  <div className="prakriti-score-track">
                    <motion.div
                      className="prakriti-score-fill prakriti-score-fill-vata"
                      initial={{ width: 0 }}
                      animate={{ width: `${result.vataScore}%` }}
                      transition={{ delay: 0.3, duration: 0.6 }}
                    />
                  </div>
                  <span className="prakriti-score-pct">{result.vataScore}%</span>
                </div>
                <div className="prakriti-score-row">
                  <span className="prakriti-score-label">
                    <Flame size={18} /> Pitta
                  </span>
                  <div className="prakriti-score-track">
                    <motion.div
                      className="prakriti-score-fill prakriti-score-fill-pitta"
                      initial={{ width: 0 }}
                      animate={{ width: `${result.pittaScore}%` }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                    />
                  </div>
                  <span className="prakriti-score-pct">{result.pittaScore}%</span>
                </div>
                <div className="prakriti-score-row">
                  <span className="prakriti-score-label">
                    <Droplets size={18} /> Kapha
                  </span>
                  <div className="prakriti-score-track">
                    <motion.div
                      className="prakriti-score-fill prakriti-score-fill-kapha"
                      initial={{ width: 0 }}
                      animate={{ width: `${result.kaphaScore}%` }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                    />
                  </div>
                  <span className="prakriti-score-pct">{result.kaphaScore}%</span>
                </div>
              </div>

              {/* Rich Dosha Profiles Guidelines */}
              <div style={{ marginTop: '2rem', marginBottom: '2rem', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <h3 style={{ fontSize: '1.25rem', margin: 0, borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                  Dosha Characteristics & Guidelines
                </h3>
                {getDoshaProfiles(result.dominantPrakriti).map((profile) => (
                  <div key={profile.name} style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '1.25rem', borderRadius: '12px', border: '1px solid rgba(0, 212, 255, 0.15)' }}>
                    <h4 style={{ color: 'var(--primary-color)', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
                      {profile.name} Constitution
                    </h4>
                    <p style={{ fontSize: '0.925rem', color: 'var(--text-secondary)', marginBottom: '0.75rem', lineHeight: '1.5' }}>{profile.description}</p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Qualities:</strong> {profile.qualities}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Dietary Guidance:</strong> {profile.diet}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 0 }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Lifestyle Advice:</strong> {profile.lifestyle}
                    </p>
                  </div>
                ))}
              </div>

              <div className="prakriti-result-actions">
                <Link to="/treatment/recommendations" className="btn btn-primary">
                  Get recommendations
                </Link>
                <Link to="/treatment" className="btn btn-outline">Back to dashboard</Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="treatment-page prakriti-assess-page">
      <section className="hero hero-compact">
        <div className="container">
          <div className="hero-content text-center">
            <h1>Discover your Prakriti</h1>
            <p>
              One question at a time — choose the option that feels most like you for <strong>{currentPatient.name}</strong>.
            </p>
          </div>
        </div>
      </section>
      <TreatmentSubHeader />

      <section className="section prakriti-section">
        <div className="container container-narrow">
          <div className="treatment-breadcrumb">
            <Link to="/treatment">AYUSH Care</Link>
            <span> / Prakriti Assessment</span>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading questions...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-outline" onClick={fetchQuestions}>Retry</button>
            </div>
          )}

          {!loading && !error && totalQuestions > 0 && (
            <>
              <div className="prakriti-progress-wrap">
                <div className="prakriti-progress-info">
                  <span className="prakriti-progress-text">
                    Question {currentIndex + 1} of {totalQuestions}
                  </span>
                  <span className="prakriti-progress-answered">{answeredCount} answered</span>
                </div>
                <div className="prakriti-progress-bar">
                  <motion.div
                    className="prakriti-progress-fill"
                    initial={false}
                    animate={{ width: `${progress}%` }}
                    transition={{ type: 'spring', stiffness: 80, damping: 20 }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  initial={{ opacity: 0, x: direction * 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -40 }}
                  transition={{ duration: 0.25 }}
                  className="prakriti-question-block"
                >
                  {currentQuestion && (
                    <>
                      {currentQuestion.category && (
                        <span className="prakriti-question-category">{currentQuestion.category}</span>
                      )}
                      <h2 className="prakriti-question-text">{currentQuestion.question}</h2>
                      <p className="prakriti-question-hint">Tap the option that best describes you</p>
                      <div className="prakriti-options">
                        {['vata', 'pitta', 'kapha'].map((choice) => {
                          const opt =
                            currentQuestion[`${choice}Option`] ||
                            currentQuestion[`${choice.charAt(0).toUpperCase() + choice.slice(1)}Option`];
                          if (!opt) return null;
                          const config = DOSHA_CONFIG[choice];
                          const Icon = config.icon;
                          const isSelected = answers[currentQuestion.id] === choice;
                          return (
                            <motion.button
                              key={choice}
                              type="button"
                              className={`prakriti-option-card prakriti-option-card--${choice} ${isSelected ? 'prakriti-option-card--selected' : ''}`}
                              onClick={() => handleAnswer(currentQuestion.id, choice)}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                            >
                              <span className="prakriti-option-icon">
                                <Icon size={28} />
                              </span>
                              <span className="prakriti-option-content">
                                <span className="prakriti-option-label">{config.label}</span>
                                <span className="prakriti-option-text">{opt}</span>
                              </span>
                              {isSelected && (
                                <motion.span
                                  className="prakriti-option-check"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                >
                                  <Check size={20} strokeWidth={3} />
                                </motion.span>
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {submitError && (
                <div className="error-state prakriti-submit-error">
                  <AlertCircle size={18} /> {submitError}
                </div>
              )}

              <div className="prakriti-nav">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={goPrev}
                  disabled={currentIndex === 0}
                  aria-label="Previous question"
                >
                  <ChevronLeft size={20} /> Previous
                </button>
                <span className="prakriti-nav-dots">
                  {questions.slice(0, Math.min(10, totalQuestions)).map((_, i) => (
                    <span
                      key={i}
                      className={`prakriti-dot ${i === currentIndex ? 'prakriti-dot--active' : ''} ${answers[questions[i]?.id] ? 'prakriti-dot--answered' : ''}`}
                      aria-hidden
                    />
                  ))}
                  {totalQuestions > 10 && (
                    <span className="prakriti-dot-more">+{totalQuestions - 10}</span>
                  )}
                </span>
                {currentIndex < totalQuestions - 1 ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={goNext}
                    disabled={!answers[currentQuestion?.id]}
                    aria-label="Next question"
                  >
                    Next <ChevronRight size={20} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={answeredCount < totalQuestions || submitting}
                  >
                    {submitting ? 'Submitting...' : 'See my result'}
                  </button>
                )}
              </div>
            </>
          )}

          {!loading && !error && totalQuestions === 0 && (
            <div className="treatment-empty">
              <p>No assessment questions available.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PrakritiAssess;
